import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

// A compact badge-styled dropdown for inline status / priority pickers.
// options: [{ value, label, badgeClass, disabled }]
const BadgeSelect = ({
    value,
    label,
    badgeClass = 'tw-bg-gray-100 tw-text-gray-700',
    options = [],
    onChange,
    disabled = false,
    loading = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [style, setStyle] = useState({});
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setStyle({ position: 'fixed', top: rect.bottom + 6, left: rect.left, minWidth: Math.max(rect.width, 160), zIndex: 9999 });
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        updatePosition();
        const onDocClick = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setIsOpen(false);
        };
        const onMove = () => updatePosition();
        document.addEventListener('mousedown', onDocClick);
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [isOpen, updatePosition]);

    const pick = (opt) => {
        if (opt.disabled) return;
        setIsOpen(false);
        if (String(opt.value) !== String(value)) onChange?.(opt.value);
    };

    const menu = isOpen ? createPortal(
        <div ref={menuRef} style={style}
            className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-shadow-xl tw-py-1 tw-max-h-64 tw-overflow-y-auto">
            {options.map((opt) => {
                const selected = String(opt.value) === String(value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        disabled={opt.disabled}
                        onClick={() => pick(opt)}
                        className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-2.5 tw-py-1.5 tw-text-left tw-transition-colors ${
                            opt.disabled ? 'tw-opacity-40 tw-cursor-not-allowed' : 'hover:tw-bg-gray-50 tw-cursor-pointer'
                        } ${selected ? 'tw-bg-blue-50/60' : ''}`}
                    >
                        <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${opt.badgeClass || 'tw-bg-gray-100 tw-text-gray-700'}`}>
                            {opt.label}
                        </span>
                        {selected
                            ? <Icon icon="mdi:check" className="tw-text-blue-600 tw-text-sm tw-flex-shrink-0" />
                            : opt.disabled ? <Icon icon="mdi:lock-outline" className="tw-text-gray-300 tw-text-xs tw-flex-shrink-0" /> : <span className="tw-w-4" />}
                    </button>
                );
            })}
        </div>,
        document.body,
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled || loading}
                onClick={() => setIsOpen((o) => !o)}
                className={`tw-inline-flex tw-items-center tw-gap-1 tw-pl-2 tw-pr-1.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold tw-transition tw-ring-1 tw-ring-inset tw-ring-transparent ${badgeClass} ${
                    disabled ? 'tw-cursor-default' : 'tw-cursor-pointer hover:tw-ring-black/10'
                } ${isOpen ? 'tw-ring-black/20' : ''}`}
            >
                {loading ? <Icon icon="mdi:loading" className="tw-animate-spin tw-text-xs" /> : label}
                {!disabled && !loading && (
                    <Icon icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} className="tw-text-current tw-text-[13px] tw-opacity-70" />
                )}
            </button>
            {menu}
        </>
    );
};

export default BadgeSelect;
